/**
 * useUserDetails.ts
 *
 * Hook for loading and caching the current user's profile details and image
 * from the core user API. Exposes loading state and error for UI feedback.
 */

// ============= Imports =============

import { useCallback, useState } from 'react';

import { AuthContextProps } from '@/contexts/AuthContext';
import { getUser } from '../apis/core/user/get/getUser';
import { getUserImage } from '../apis/core/user/get/getUserImage';

// ============= Types =============

/**
 * User profile details returned by the core user API.
 */
interface UserDetails {
  id: string;
  name: string;
  jobTitle: string;
  username: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  department: string;
  companyName: string;
  country: string;
  office: string;
  proNoun: string;
  isOwner: boolean;
}

/**
 * Return type of useUserDetails.
 */
export interface UseUserDetailsReturn {
  userDetails: UserDetails | null;
  userImage: string | null;
  getUserDetails: () => Promise<void>;
  getUserImageDetails: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// ============= Constants =============

/** Default image dimension requested from the user image API. */
const DEFAULT_IMAGE_DIMENSION = '96';

// ============= Utilities =============

/**
 * Converts an unknown error to a user-facing error message.
 *
 * @param error - Caught error (unknown).
 * @returns Error message string.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

// ============= Hook =============

/**
 * Fetches and caches the current user's details and profile image.
 *
 * - getUserDetails: fetches profile from core user API (requires getAccessToken).
 * - getUserImageDetails: fetches profile image; requires userDetails.id (call
 *   getUserDetails first or ensure userDetails is loaded).
 * - Loading and error state are shared; call one fetcher at a time for clear UX.
 *
 * @param getAccessToken - Auth context function to obtain access token.
 * @returns User details, image, fetch functions, loading state, and error.
 */
export const useUserDetails = (
  getAccessToken: AuthContextProps['getAccessToken']
): UseUserDetailsReturn => {
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userImage, setUserImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const details = await getUser({ getAccessToken });
      setUserDetails(details);
    } catch (err) {
      const message = getErrorMessage(err);
      console.error('Error getting user details:', err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  const getUserImageDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!userDetails?.id) {
        setError('User ID is required');
        return;
      }

      const image = await getUserImage({
        getAccessToken,
        userId: userDetails.id,
        dimension: DEFAULT_IMAGE_DIMENSION,
      });
      setUserImage(image ?? null);
    } catch (err) {
      const message = getErrorMessage(err);
      console.error('Error getting user image:', err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [userDetails?.id, getAccessToken]);

  return {
    userDetails,
    userImage,
    getUserDetails,
    getUserImageDetails,
    isLoading,
    error,
  };
};
